import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import appConfig from "@config";
import {
  ExternalLink,
  FileText,
  Loader2,
  Maximize2,
  Sparkles,
} from "lucide-react";
import { useState } from "react";

export default function HomeContactCard() {
  const [isOpen, setIsOpen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  // Retrieve Google Form URL from config.json
  const configuredUrl = (
    appConfig as { GOOGLE_FORM_URL?: string }
  )?.GOOGLE_FORM_URL?.trim();

  // Helper to ensure Google Forms loads smoothly in embedded iframe mode
  const getEmbedUrl = (url?: string): string => {
    if (!url) return "";
    let clean = url;
    if (clean.includes("forms.gle")) {
      return clean;
    }
    if (clean.includes("/edit")) {
      clean = clean.replace(/\/edit.*$/, "/viewform");
    }
    if (!clean.includes("embedded=true")) {
      clean = clean.includes("?")
        ? `${clean}&embedded=true`
        : `${clean}?embedded=true`;
    }
    return clean;
  };

  const finalFormUrl = configuredUrl || "";
  const embedUrl = getEmbedUrl(finalFormUrl);

  const handleOpenModal = () => {
    setIframeLoading(true);
    setIsOpen(true);
  };

  return (
    <>
      {/* ─────────────────────────────────────────────────────────────
          INVITATION CARD (LIGHT PREMIUM CARD)
          Crisp white background with warm golden & navy accents
         ───────────────────────────────────────────────────────────── */}
      <div className="rounded-3xl bg-white text-slate-900 border border-slate-200/90 shadow-2xl p-5 sm:p-7 relative overflow-hidden transition-all duration-300 hover:shadow-amber-500/10 hover:border-amber-400/60 group">
        {/* Soft Golden Ambient Corner Glow */}
        <div className="absolute -right-10 -bottom-10 w-44 h-44 bg-amber-400/15 rounded-full blur-2xl group-hover:bg-amber-400/25 transition-all pointer-events-none" />
        <div className="absolute -left-10 -top-10 w-36 h-36 bg-sky-400/10 rounded-full blur-2xl pointer-events-none" />

        {/* Card Content */}
        <div className="space-y-3 pt-3 relative z-10">
          <div className="space-y-1">
            <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
              <span>Formulaire de Contact & Demandes</span>
            </h3>
            <p className="text-slate-600 text-xl sm:text-xl leading-relaxed">
              Mariage, baptême, présentation d'enfant, prière, location de
              salle, etc.
            </p>
          </div>

          {/* Action Buttons Row */}
          <div className="pt-2 flex flex-col sm:flex-row items-stretch gap-2.5">
            <Button
              className="flex-1 bg-amber-400 hover:bg-amber-500 text-slate-950 font-black py-3 px-5 rounded-xl shadow-md transition-all flex items-center justify-center gap-2 text-xs sm:text-sm"
              onClick={handleOpenModal}
            >
              <FileText className="w-4 h-4 text-slate-950 shrink-0" />
              <span>Remplir le formulaire</span>
            </Button>

            {/* <Button
              variant="outline"
              className="border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-bold px-4 py-3 rounded-xl text-xs sm:text-sm flex items-center justify-center gap-1.5 transition-all shadow-sm shrink-0"
              onClick={handleOpenExternal}
              title="Ouvrir le Google Form dans un nouvel onglet"
            >
              <ExternalLink className="w-3.5 h-3.5 text-slate-700 shrink-0" />
              <span>Google Forms</span>
            </Button> */}
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          LIGHT MODAL DIALOG (PURE GOOGLE FORMS VIEWER)
          Light, clean styling with seamless Google Form integration
         ───────────────────────────────────────────────────────────── */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="w-[96vw] max-w-4xl max-h-[92vh] bg-white border border-slate-200 text-slate-900 p-0 rounded-3xl shadow-2xl flex flex-col overflow-hidden">
          {/* Top Decorative Gradient Line */}
          <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-sky-400 to-amber-400 shrink-0" />

          {/* Modal Header */}
          <div className="p-4 sm:p-5 bg-slate-50/90 border-b border-slate-200 shrink-0">
            <DialogHeader className="text-left space-y-1">
              <div className="flex items-center justify-between gap-3 pr-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center border border-amber-200 shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <DialogTitle className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                      Formulaire de Contact & Demandes
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 font-medium">
                      Église de Dieu Garizim • Service pastoral officiel
                    </DialogDescription>
                  </div>
                </div>

                {/* Open in full tab button */}
                {finalFormUrl && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      window.open(finalFormUrl, "_blank", "noopener,noreferrer")
                    }
                    className="hidden sm:inline-flex items-center gap-1.5 border-slate-300 bg-white hover:bg-slate-100 text-slate-800 text-xs px-3 h-8 rounded-xl font-semibold shrink-0 shadow-sm"
                  >
                    <Maximize2 className="w-3.5 h-3.5 text-slate-700" />
                    <span>Plein écran ↗</span>
                  </Button>
                )}
              </div>
            </DialogHeader>
          </div>

          {/* Modal Body: Google Form Iframe Container */}
          <div className="flex-1 relative bg-slate-100/60 min-h-[500px] sm:min-h-[640px] overflow-hidden flex flex-col">
            {finalFormUrl ? (
              <>
                {iframeLoading && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white text-slate-600">
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                    <p className="text-xs text-slate-500 font-semibold">
                      Chargement du formulaire Google...
                    </p>
                  </div>
                )}
                <iframe
                  src={embedUrl}
                  title="Formulaire de Contact & Demandes Google Forms"
                  className="w-full flex-1 h-full min-h-[520px] sm:min-h-[640px] border-0 bg-white"
                  onLoad={() => setIframeLoading(false)}
                />
              </>
            ) : (
              /* If URL is not set yet in config.json, show clear instructions in light theme */
              <div className="p-8 sm:p-12 text-center flex-1 flex flex-col items-center justify-center space-y-4 bg-white">
                <div className="w-16 h-16 rounded-3xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center mx-auto shadow-sm">
                  <FileText className="w-8 h-8" />
                </div>
                <div className="max-w-md space-y-2">
                  <h4 className="text-base sm:text-lg font-black text-slate-900">
                    Formulaire de Contact & Demandes
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Veuillez renseigner le lien public de votre Google Form dans{" "}
                    <code className="text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 font-mono text-xs">
                      config.json
                    </code>{" "}
                    sous{" "}
                    <code className="text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 font-mono text-xs">
                      GOOGLE_FORM_URL
                    </code>{" "}
                    pour l'afficher automatiquement ici dans la fenêtre.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="p-3.5 px-6 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-600 shrink-0">
            <span className="flex items-center gap-1.5 font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Contact direct avec l'équipe pastorale
            </span>

            {false && finalFormUrl && (
              <a
                href={finalFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-700 hover:text-amber-900 font-bold hover:underline inline-flex items-center gap-1"
              >
                <span>Ouvrir dans Google Forms</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
